import React, { useState, useEffect, useCallback } from 'react';
import { getNivelesFidelidad, deleteNivelFidelidad } from '../services/nivelFidelidadService';
import { Link, useNavigate } from 'react-router-dom';
import { PlusCircle, ArrowLeft, RefreshCw, Search, ChevronLeft, ChevronRight, Award, Trash2, Edit } from 'lucide-react';
import { toast, Toaster } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

const NivelFidelidadList = () => {
    const [niveles, setNiveles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();
    const { token } = useAuth();

    const fetchNiveles = useCallback(async () => {
        try {
            setLoading(true);
            const data = await getNivelesFidelidad(token);
            setNiveles(data);
        } catch (error) {
            toast.error('Error al cargar niveles de fidelidad');
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchNiveles();
    }, [fetchNiveles]);

    const handleDelete = async (id) => {
        if (!window.confirm('¿Está seguro de eliminar este nivel de fidelidad?')) return;
        try {
            await deleteNivelFidelidad(token, id);
            toast.success('Nivel de fidelidad eliminado correctamente');
            fetchNiveles();
        } catch (error) {
            toast.error('Error al eliminar. Verifique dependencias.');
        }
    };

    const filteredNiveles = niveles.filter(n =>
        (n.codigo?.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (n.descripcion?.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    return (
        <div className="max-w-[98%] mx-auto bg-white shadow-sm border border-gray-200 mb-10 overflow-hidden rounded-sm animate-in fade-in duration-500">
            <Toaster position="top-right" />

            {/* Header Enterprise Style (Slate) */}
            <div className="bg-slate-700 p-3 flex justify-between items-center text-white">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/parametrizacion/personas')}
                        className="p-1 hover:bg-slate-600 rounded transition-colors text-slate-300 hover:text-white"
                        title="Volver"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <h1 className="text-lg font-semibold tracking-tight uppercase italic">Mantenimiento: Niveles de Fidelidad</h1>
                </div>

                <Link
                    to="/parametrizacion/niveles-fidelidad/nuevo"
                    className="flex items-center gap-1.5 transition-all rounded-sm px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white shadow-sm text-[0.85rem] font-bold uppercase"
                >
                    <PlusCircle className="w-4 h-4" />
                    NUEVO NIVEL
                </Link>
            </div>

            {/* Controles */}
            <div className="p-3 bg-gray-50 border-b border-gray-200 flex flex-wrap justify-between items-center gap-4">
                <div className="flex items-center gap-4">
                    <div className="relative group">
                        <input
                            type="text"
                            placeholder="Buscar por código o descripción..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="bg-white border border-gray-300 pl-8 pr-3 py-1.5 w-64 focus:ring-1 focus:ring-blue-500 outline-none text-[0.875rem] rounded-sm shadow-sm uppercase font-bold"
                        />
                        <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-2.5 group-focus-within:text-blue-500" />
                    </div>
                </div>

                <div className="text-[11px] font-bold text-gray-400 uppercase tracking-widest flex items-center gap-2">
                    {loading && <RefreshCw className="w-3 h-3 animate-spin text-blue-500" />}
                    PARÁMETROS DE FIDELIZACIÓN Y DESCUENTOS
                </div>
            </div>

            {/* Tabla */}
            <div className="overflow-x-auto relative">
                <table className="min-w-full text-left border-collapse table-fixed">
                    <thead>
                        <tr className="bg-[#f8fafc] border-b-2 border-slate-200 text-gray-700 text-[10px] font-bold uppercase tracking-wider">
                            <th className="px-3 py-2 w-16 text-center border-r border-gray-200 sticky left-0 bg-[#f8fafc] z-10">ID</th>
                            <th className="px-4 py-2 w-32 border-r border-gray-200">Código</th>
                            <th className="px-4 py-2 border-r border-gray-200">Descripción Operativa</th>
                            <th className="px-4 py-2 w-48 border-r border-gray-200 text-center">Descuento Tasa (%)</th>
                            <th className="px-4 py-2 w-48 border-r border-gray-200 text-center">Rango Puntos</th>
                            <th className="px-3 py-2 w-32 text-center sticky right-0 bg-[#f8fafc] shadow-[-4px_0_10px_-4px_rgba(0,0,0,0.1)] border-l border-gray-200 z-10">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="text-[12px] text-gray-600">
                        {loading && niveles.length === 0 ? (
                            <tr><td colSpan="6" className="px-4 py-8 text-center opacity-50 font-bold uppercase italic border-b border-gray-200">Cargando datos...</td></tr>
                        ) : filteredNiveles.length === 0 ? (
                            <tr><td colSpan="6" className="px-4 py-8 text-center text-gray-400 font-bold uppercase italic border-b border-gray-200">No se encontraron resultados.</td></tr>
                        ) : (
                            filteredNiveles.map((n) => (
                                <tr key={n.id} className="border-b border-gray-100 hover:bg-[#f1f5f9] transition-colors group">
                                    <td className="px-3 py-2 text-center font-mono text-[11px] text-gray-400 border-r border-gray-100 sticky left-0 bg-white group-hover:bg-[#f1f5f9] z-10">{n.id}</td>
                                    <td className="px-4 py-2 border-r border-gray-100">
                                        <div className="flex items-center gap-2">
                                            <div className="p-1 bg-indigo-50 rounded">
                                                <Award className="w-3.5 h-3.5 text-indigo-500" />
                                            </div>
                                            <span className="font-black text-gray-800 tracking-widest">{n.codigo}</span>
                                        </div>
                                    </td>
                                    <td className="px-4 py-2 border-r border-gray-100 font-bold text-gray-700 uppercase italic truncate">
                                        {n.descripcion}
                                    </td>
                                    <td className="px-4 py-2 border-r border-gray-100 text-center font-black text-emerald-600">
                                        {n.descuentoTasa ? `${Number(n.descuentoTasa).toFixed(2)} %` : '0.00 %'}
                                    </td>
                                    <td className="px-4 py-2 border-r border-gray-100 text-center">
                                        <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-slate-100 border border-slate-200 rounded-full font-black text-slate-700 text-[10px]">
                                            <span>{n.puntosMin ?? 0}</span>
                                            <span className="text-slate-400">-</span>
                                            <span>{n.puntosMax ?? 0}</span>
                                        </div>
                                    </td>
                                    <td className="px-3 py-2 text-center sticky right-0 bg-white shadow-[-4px_0_10px_-4px_rgba(0,0,0,0.1)] border-l border-gray-200 group-hover:bg-[#f1f5f9] z-10">
                                        <div className="flex justify-center items-center gap-2 font-semibold text-xs">
                                            <button
                                                onClick={() => navigate(`/parametrizacion/niveles-fidelidad/${n.id}/editar`)}
                                                className="text-blue-600 hover:text-blue-800 transition-colors uppercase flex items-center gap-0.5"
                                            >
                                                <Edit className="w-3.5 h-3.5" />
                                                EDITAR
                                            </button>
                                            <span className="text-gray-300">|</span>
                                            <button
                                                onClick={() => handleDelete(n.id)}
                                                className="text-red-600 hover:text-red-800 transition-colors uppercase flex items-center gap-0.5"
                                            >
                                                <Trash2 className="w-3.5 h-3.5" />
                                                ELIMINAR
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Paginación */}
            <div className="p-3 bg-gray-50 flex flex-col sm:flex-row justify-between items-center gap-2 border-t border-gray-200 text-[0.875rem] text-gray-500 uppercase font-bold">
                <div>
                    Mostrando <span className="text-gray-700">{filteredNiveles.length}</span> registros de <span className="text-gray-700">{niveles.length}</span>
                </div>

                <div className="flex items-center gap-1">
                    <button disabled className="px-2 py-1 border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-30 rounded-sm italic text-xs">P Primero</button>
                    <button disabled className="px-2 py-1 border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-30 rounded-sm"><ChevronLeft className="w-4 h-4" /></button>
                    <div className="px-4 py-1 border border-blue-600 bg-blue-600 text-white font-bold text-xs rounded-sm">1</div>
                    <span className="text-[10px] font-bold text-gray-400 px-1 uppercase italic ml-1">de 1</span>
                    <button disabled className="px-2 py-1 border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-30 rounded-sm"><ChevronRight className="w-4 h-4" /></button>
                    <button disabled className="px-2 py-1 border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-30 rounded-sm italic text-xs">U Último</button>
                </div>
            </div>
        </div>
    );
};

export default NivelFidelidadList;
